class UgcVideos extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.currentAutoIndex = 0;
    this.autoTimer = null;
    this.isHovering = false;
    this.observer = null;
    this.isVisible = false;
    this.modal = null;
    this.modalVideo = null;
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.ugc-card'));
    if (!this.cards.length) return;

    this.playMode = this.dataset.playMode || 'hover';
    this.previewDuration = parseInt(this.dataset.previewDuration, 10) || 10;

    this.grid = this.querySelector('.ugc-videos__grid');

    this.setupIntersectionObserver();
    this.setupHoverEvents();
    this.setupClickEvents();
    this.setupDragScroll();
    this.createModal();
  }

  disconnectedCallback() {
    this.stopAll();
    if (this.autoTimer) clearTimeout(this.autoTimer);
    if (this.observer) this.observer.disconnect();
    if (this.modal) this.modal.remove();
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  /* ---- Intersection Observer ---- */
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible && this.playMode === 'auto' && !this.isHovering) {
            this.startAutoPlay();
          } else if (!this.isVisible) {
            this.stopAll();
            if (this.autoTimer) clearTimeout(this.autoTimer);
          }
        });
      },
      { threshold: 0.3 }
    );
    this.observer.observe(this);
  }

  /* ---- Hover Events (both modes) ---- */
  setupHoverEvents() {
    this.cards.forEach((card, index) => {
      card.addEventListener('mouseenter', () => {
        this.isHovering = true;
        if (this.autoTimer) clearTimeout(this.autoTimer);
        this.clearPendingEnded();
        this.stopAll();
        this.playPreview(card);
      });

      card.addEventListener('mouseleave', () => {
        this.isHovering = false;
        if (this.playMode === 'auto' && this.isVisible) {
          const video = card.querySelector('.ugc-card__preview');
          if (!video) {
            this.currentAutoIndex = (index + 1) % this.cards.length;
            this.startAutoPlay();
            return;
          }
          video.loop = false;
          const handler = () => {
            video.removeEventListener('ended', handler);
            video.loop = true;
            this._pendingEndedHandler = null;
            this.stopPreview(card);
            if (this.isHovering || !this.isVisible) return;
            this.currentAutoIndex = (index + 1) % this.cards.length;
            this.startAutoPlay();
          };
          video.addEventListener('ended', handler);
          this._pendingEndedHandler = { video, handler };
        } else {
          this.stopPreview(card);
        }
      });
    });
  }

  clearPendingEnded() {
    if (!this._pendingEndedHandler) return;
    const { video, handler } = this._pendingEndedHandler;
    video.removeEventListener('ended', handler);
    video.loop = true;
    this._pendingEndedHandler = null;
  }

  /* ---- Click → Modal ---- */
  setupClickEvents() {
    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.openModal(card));
    });
  }

  /* ---- Preview Playback ---- */
  playPreview(card) {
    const video = card.querySelector('.ugc-card__preview');
    if (!video) return;

    // Lazy-load src
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
    }

    video.currentTime = 0;
    const playPromise = video.play();
    if (playPromise) {
      playPromise
        .then(() => {
          video.classList.add('ugc-card__preview--playing');
          card.classList.add('ugc-card--playing');
        })
        .catch(() => {});
    }
  }

  stopPreview(card) {
    const video = card.querySelector('.ugc-card__preview');
    if (!video) return;

    video.pause();
    video.currentTime = 0;
    video.classList.remove('ugc-card__preview--playing');
    card.classList.remove('ugc-card--playing');
  }

  stopAll() {
    this.clearPendingEnded();
    this.cards.forEach((card) => this.stopPreview(card));
  }

  /* ---- Auto-Sequential Mode ---- */
  startAutoPlay() {
    if (this.autoTimer) clearTimeout(this.autoTimer);
    if (!this.isVisible || this.isHovering) return;

    const card = this.cards[this.currentAutoIndex];
    if (!card) return;

    this.stopAll();
    this.playPreview(card);

    this.autoTimer = setTimeout(() => {
      this.stopPreview(card);
      this.currentAutoIndex = (this.currentAutoIndex + 1) % this.cards.length;
      this.startAutoPlay();
    }, this.previewDuration * 1000);
  }

  /* ---- Drag to scroll (desktop) ---- */
  setupDragScroll() {
    if (!this.grid) return;
    let isDown = false;
    let startX;
    let scrollLeft;
    let hasDragged = false;

    this.grid.addEventListener('mousedown', (e) => {
      isDown = true;
      hasDragged = false;
      startX = e.pageX - this.grid.offsetLeft;
      scrollLeft = this.grid.scrollLeft;
      this.grid.classList.add('ugc-videos__grid--dragging');
    });

    this.grid.addEventListener('mouseleave', () => {
      if (!isDown) return;
      isDown = false;
      this.grid.classList.remove('ugc-videos__grid--dragging');
    });

    this.grid.addEventListener('mouseup', () => {
      isDown = false;
      this.grid.classList.remove('ugc-videos__grid--dragging');
    });

    this.grid.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - this.grid.offsetLeft;
      const walk = x - startX;
      if (Math.abs(walk) > 5) hasDragged = true;
      this.grid.scrollLeft = scrollLeft - walk;
    });

    // Prevent click on cards after drag
    this.grid.addEventListener('click', (e) => {
      if (hasDragged) {
        e.stopPropagation();
        hasDragged = false;
      }
    }, true);
  }

  /* ---- Modal ---- */
  createModal() {
    this.modal = document.createElement('div');
    this.modal.className = 'ugc-modal';
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.setAttribute('aria-label', 'Video player');
    this.modal.innerHTML = `
      <div class="ugc-modal__content">
        <button class="ugc-modal__close" aria-label="Close">
          <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <video class="ugc-modal__video" controls playsinline></video>
      </div>
    `;
    document.body.appendChild(this.modal);

    this.modalVideo = this.modal.querySelector('.ugc-modal__video');

    // Close handlers
    this.modal.querySelector('.ugc-modal__close').addEventListener('click', () => this.closeModal());
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });

    this.handleKeyDown = (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('ugc-modal--open')) {
        this.closeModal();
      }
    };
    document.addEventListener('keydown', this.handleKeyDown);
  }

  openModal(card) {
    const fullVideoUrl = card.dataset.fullVideo;
    if (!fullVideoUrl || !this.modalVideo) return;

    // Stop all previews
    this.stopAll();
    if (this.autoTimer) clearTimeout(this.autoTimer);

    this.modalVideo.src = fullVideoUrl;
    this.modal.classList.add('ugc-modal--open');
    document.body.style.overflow = 'hidden';

    this.modalVideo.play().catch(() => {});
    this.modal.querySelector('.ugc-modal__close').focus();
  }

  closeModal() {
    this.modalVideo.pause();
    this.modalVideo.removeAttribute('src');
    this.modalVideo.load();
    this.modal.classList.remove('ugc-modal--open');
    document.body.style.overflow = '';

    // Resume auto if applicable
    if (this.playMode === 'auto' && this.isVisible && !this.isHovering) {
      this.startAutoPlay();
    }
  }
}

customElements.define('ugc-videos', UgcVideos);
